/* i18next, with the tables split by namespace and by file.
 *
 *   locales/<lang>/ui.json      chrome: meter, buttons, legends, about copy
 *   locales/<lang>/path.json    the curriculum: chapters, blocks, exercises
 *   locales/<lang>/films.json   the animated diagrams: labels and captions
 *   locales/<lang>/topics.json  bibliography subject headings
 *   locales/<lang>/game.json    the bonus stage ladder
 *   locales/<lang>/routes.json  the subject layer: subjects, routes, who each is for
 *
 * The active language comes from i18next's own detector: a previously chosen
 * language, else the browser's, else English — and it is cached back to
 * localStorage under LANG_KEY.
 */
import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next, useTranslation } from 'react-i18next';
import { useCallback } from 'react';
import { LANG_KEY } from '../data/srs';
import ui_en from './locales/en/ui.json';
import path_en from './locales/en/path.json';
import films_en from './locales/en/films.json';
import topics_en from './locales/en/topics.json';
import game_en from './locales/en/game.json';
import routes_en from './locales/en/routes.json';
import ui_fr from './locales/fr/ui.json';
import path_fr from './locales/fr/path.json';
import films_fr from './locales/fr/films.json';
import topics_fr from './locales/fr/topics.json';
import game_fr from './locales/fr/game.json';
import routes_fr from './locales/fr/routes.json';
import type { Lang } from '../types';

export const NAMESPACES = ['ui', 'path', 'films', 'topics', 'game', 'routes'] as const;
export type Namespace = (typeof NAMESPACES)[number];

export const SUPPORTED_LANGS: Lang[] = ['en', 'fr'];
export const DEFAULT_NS: Namespace = 'ui';

const resources = {
  en: { ui: ui_en, path: path_en, films: films_en, topics: topics_en, game: game_en, routes: routes_en },
  fr: { ui: ui_fr, path: path_fr, films: films_fr, topics: topics_fr, game: game_fr, routes: routes_fr },
} as const;

export function isLang(value: string | undefined): value is Lang {
  return value === 'en' || value === 'fr';
}

export function initI18n(): typeof i18next {
  void i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      ns: [...NAMESPACES],
      defaultNS: DEFAULT_NS,
      fallbackLng: 'en',
      supportedLngs: SUPPORTED_LANGS,
      nonExplicitSupportedLngs: true,
      load: 'languageOnly',
      detection: {
        order: ['localStorage', 'navigator', 'htmlTag'],
        lookupLocalStorage: LANG_KEY,
        caches: ['localStorage'],
      },
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  return i18next;
}

export function currentLang(): Lang {
  return isLang(i18next.language) ? i18next.language : 'en';
}

/* Returns the change promise so a caller that must wait (tests, the document
   language effect) can; the toggle does not need to. */
export function setLang(lang: Lang): Promise<void> {
  return i18next.changeLanguage(lang).then(() => undefined);
}

export function useLang(): Lang {
  const { i18n } = useTranslation();
  return isLang(i18n.language) ? i18n.language : 'en';
}

/* Translate a dynamic content key. Keys come from the generated data modules,
 * so they are not literals; the key-coverage test is what guarantees they all
 * resolve in both languages. */
export function useKey(): (key: string) => string {
  const { t, i18n } = useTranslation();
  const lang = isLang(i18n.language) ? i18n.language : 'en';
  return useCallback((key: string) => t(key, { lng: lang }), [t, lang]);
}
